import { cursorFromRow, type WorkbenchCursor } from './keysetCursor';

/** Minimum, das der Puffer von einer Produktzeile braucht. */
export interface BufferItem {
  id: string;
  last_optimized_at: string | null;
}

/**
 * Sitzungsstabiler Puffer des Reviewers.
 *
 * `items` ist die Reihenfolge, die der Nutzer in DIESER Sitzung sieht. Sie
 * wird nur angehaengt, nie umsortiert und nie gekuerzt, auch wenn Produkte
 * serverseitig aus `pending_review` verschwinden. Genau das macht Vor und
 * Zurueck stabil, waehrend die Servermenge schrumpft.
 */
export interface ReviewBufferState<T extends BufferItem> {
  items: T[];
  index: number;
  cursor: WorkbenchCursor | null;
  exhausted: boolean;
  /**
   * Identity-Token der aktuellen Puffer-"Session" (z.B. `${feedId}|${search}`).
   * Ein Aufrufer, der die Population wechselt (Feed- oder Suchwechsel), ruft
   * `emptyBuffer(neueIdentity)` auf. `appendPage` verwirft danach jede Antwort,
   * deren Identity nicht mehr mit `state.identity` uebereinstimmt — das faengt
   * die Spaetantwort einer bereits abgeloesten Seite ab, die sonst quer in den
   * frisch zurueckgesetzten Puffer eines anderen Feeds gemischt worden waere.
   * Default `''` haelt bestehende Aufrufer ohne Identity-Konzept lauffaehig
   * (Identity ist dann konstant `''` auf beiden Seiten, also nie ein Mismatch).
   */
  identity: string;
}

export function emptyBuffer<T extends BufferItem>(identity: string = ''): ReviewBufferState<T> {
  return { items: [], index: 0, cursor: null, exhausted: false, identity };
}

/**
 * Haengt eine frisch geladene Seite an. Bereits bekannte ids werden
 * uebersprungen, damit ein Refetch derselben Seite nicht dupliziert.
 * Eine Seite kleiner als `pageSize` bedeutet: es kommt nichts mehr.
 *
 * `identity` muss mit `state.identity` uebereinstimmen, sonst gehoert die
 * Antwort zu einer bereits abgeloesten Puffer-Session (Feed-/Suchwechsel
 * waehrend der Request noch unterwegs war) und wird verworfen — der Zustand
 * bleibt unveraendert.
 */
export function appendPage<T extends BufferItem>(
  state: ReviewBufferState<T>,
  page: T[],
  pageSize: number,
  identity: string = '',
): ReviewBufferState<T> {
  if (identity !== state.identity) {
    return state;
  }

  const known = new Set(state.items.map((i) => i.id));
  const fresh = page.filter((row) => !known.has(row.id));
  const items = fresh.length > 0 ? [...state.items, ...fresh] : state.items;
  const last = page.length > 0 ? page[page.length - 1] : null;

  return {
    ...state,
    items,
    cursor: last ? cursorFromRow(last) : state.cursor,
    exhausted: page.length < pageSize,
  };
}

/**
 * Eine Position vor. Darf ueber das Pufferende hinauslaufen, solange
 * nachgeladen werden kann; der Aufrufer loest dann `needsMore` aus. Ist der
 * Puffer erschoepft, bleibt die Position auf dem letzten Element stehen.
 */
export function stepForward<T extends BufferItem>(state: ReviewBufferState<T>): ReviewBufferState<T> {
  const limit = state.exhausted
    ? Math.max(0, state.items.length - 1)
    : state.items.length;
  return { ...state, index: Math.min(limit, state.index + 1) };
}

export function stepBack<T extends BufferItem>(state: ReviewBufferState<T>): ReviewBufferState<T> {
  return { ...state, index: Math.max(0, state.index - 1) };
}

/** Liegen weniger als `prefetch` Elemente vor der aktuellen Position? */
export function needsMore<T extends BufferItem>(state: ReviewBufferState<T>, prefetch: number): boolean {
  if (state.exhausted) return false;
  return state.items.length - state.index <= prefetch;
}
